import { Component, ChangeDetectionStrategy, ElementRef, signal, computed, viewChild } from "@angular/core";

import type { ICellEditorAngularComp } from "ag-grid-angular";
import type { ICellEditorParams } from "ag-grid-community";

/**
 * A searchable, infinite-scrolling AG Grid cell editor for picking one item from a data source too
 * large to load into a plain `agSelectCellEditor` dropdown up front. Generic over the option shape so
 * any table can reuse it - the caller supplies how to fetch a page/count and how to display/identify
 * an option, and gets the selected option object (or `null` if cleared) back from `getValue()`.
 */
export interface InfiniteSelectCellEditorParams<TOption, TData = unknown> extends ICellEditorParams<TData> {
  fetchCount: (search: string) => Promise<number>;
  fetchItems: (search: string, limit: number, offset: number) => Promise<TOption[]>;
  displayValue: (option: TOption) => string;
  optionId: (option: TOption) => string;
  pageSize?: number;
  /** Whether an explicit "clear" row is offered to set the value to `null`. */
  clearable?: boolean;
  clearLabel?: string;
  searchPlaceholder?: string;
  /**
   * Opts into allowing a value that doesn't exist in the fetched results yet - when supplied, a
   * "Use <search text>" row is offered (whenever the typed search doesn't exactly match an
   * existing option) that turns the raw search text into an option via this callback.
   */
  createCustomOption?: (text: string) => TOption;
}

/** The subset of `InfiniteSelectCellEditorParams` callers supply via `cellEditorParams` — the rest is filled in by AG Grid itself. */
export type InfiniteSelectCellEditorCustomParams<TOption, TData = unknown> = Pick<
  InfiniteSelectCellEditorParams<TOption, TData>,
  | "fetchCount"
  | "fetchItems"
  | "displayValue"
  | "optionId"
  | "pageSize"
  | "clearable"
  | "clearLabel"
  | "searchPlaceholder"
  | "createCustomOption"
>;

type SpecialRow = { kind: "clear" } | { kind: "custom"; label: string };

let nextInstanceId = 0;

@Component({
  selector: "infinite-select-cell-editor",
  template: `
    <div class="picker" role="combobox" aria-expanded="true" [attr.aria-owns]="listboxId" aria-haspopup="listbox">
      <input
        #searchInput
        type="text"
        class="search"
        role="searchbox"
        autocomplete="off"
        [placeholder]="searchPlaceholder()"
        [attr.aria-controls]="listboxId"
        [attr.aria-activedescendant]="activeDescendantId()"
        [value]="search()"
        (input)="onSearchInput($event)"
        (keydown)="onKeydown($event)"
      />
      <div [id]="listboxId" role="listbox" class="options" (scroll)="onScroll($event)">
        @for (row of specialRows(); track row.kind; let i = $index) {
          <div
            [id]="specialRowId(i)"
            role="option"
            class="option"
            [class.clear-option]="row.kind === 'clear'"
            [class.custom-option]="row.kind === 'custom'"
            [class.divider]="i === specialRows().length - 1"
            [class.active]="highlightedIndex() === specialIndex(i)"
            [attr.aria-selected]="highlightedIndex() === specialIndex(i)"
            (click)="row.kind === 'clear' ? clearSelection() : selectCustomValue(row.label)"
          >
            {{ row.kind === "clear" ? clearLabel() : 'Use "' + row.label + '"' }}
          </div>
        }
        @for (item of items(); track optionId(item); let i = $index) {
          <div
            [id]="optionDomId(i)"
            role="option"
            class="option"
            [class.active]="highlightedIndex() === i"
            [attr.aria-selected]="highlightedIndex() === i"
            (click)="selectItem(item)"
          >
            {{ displayValue(item) }}
          </div>
        }
        @if (loading()) {
          <div class="status">Loading…</div>
        } @else if (items().length === 0) {
          <div class="status">No matches</div>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .picker {
        display: flex;
        flex-direction: column;
        width: 280px;
        max-height: 300px;
        background: white;
        border: 1px solid rgba(0, 0, 0, 0.2);
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        overflow: hidden;
      }
      .search {
        flex: none;
        box-sizing: border-box;
        width: 100%;
        padding: 8px;
        border: none;
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
        font: inherit;
        outline: none;
      }
      .options {
        flex: 1;
        overflow-y: auto;
      }
      .option {
        padding: 6px 10px;
        cursor: pointer;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .option:hover,
      .option.active {
        background: rgba(0, 0, 0, 0.06);
      }
      .clear-option {
        font-style: italic;
        color: rgba(0, 0, 0, 0.6);
      }
      .custom-option {
        font-style: italic;
      }
      .divider {
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
      }
      .status {
        padding: 8px 10px;
        color: rgba(0, 0, 0, 0.6);
      }
    `,
  ],
})
export class InfiniteSelectCellEditor<TOption> implements ICellEditorAngularComp {
  private readonly searchInputRef = viewChild<ElementRef<HTMLInputElement>>("searchInput");

  readonly listboxId = `infinite-select-listbox-${nextInstanceId++}`;

  readonly search = signal("");
  readonly items = signal<TOption[]>([]);
  readonly totalCount = signal(0);
  readonly loading = signal(false);
  readonly highlightedIndex = signal(-1);

  readonly clearable = computed(() => this.#params.clearable ?? false);
  readonly clearLabel = computed(() => this.#params.clearLabel ?? "— Clear —");
  readonly searchPlaceholder = computed(() => this.#params.searchPlaceholder ?? "Search…");

  /** The typed search text as a creatable option, when it doesn't already match an existing item. */
  readonly customOptionLabel = computed(() => {
    if (!this.#params.createCustomOption) return null;
    const text = this.search().trim();
    if (!text) return null;
    const exists = this.items().some((item) => this.displayValue(item).toLowerCase() === text.toLowerCase());
    return exists ? null : text;
  });

  readonly specialRows = computed<SpecialRow[]>(() => {
    const rows: SpecialRow[] = [];
    if (this.clearable()) rows.push({ kind: "clear" });
    const custom = this.customOptionLabel();
    if (custom !== null) rows.push({ kind: "custom", label: custom });
    return rows;
  });

  readonly activeDescendantId = computed(() => {
    const index = this.highlightedIndex();
    if (index < 0) {
      const position = index + this.specialRows().length;
      return this.specialRows()[position] ? this.specialRowId(position) : undefined;
    }
    return this.optionDomId(index);
  });

  #params!: InfiniteSelectCellEditorParams<TOption>;
  #pageSize = 50;
  #searchDebounceTimer?: ReturnType<typeof setTimeout>;
  #selected: TOption | null | undefined = undefined;
  #requestToken = 0;

  agInit(params: InfiniteSelectCellEditorParams<TOption>): void {
    this.#params = params;
    this.#pageSize = params.pageSize ?? 50;
    this.highlightedIndex.set(this.#defaultHighlightIndex());
    void this.#loadFirstPage();
  }

  /**
   * The row to highlight by default: the last special row when any exist - `specialRows()` always
   * puts a "custom" row after "clear", so this prefers creating the typed value (the more likely
   * intent once one is offered) over the fixed "Clear" row, while keeping "Clear" as the default
   * when it's the only special row (unchanged prior behavior).
   */
  #defaultHighlightIndex(): number {
    return this.specialRows().length > 0 ? -1 : 0;
  }

  /** `undefined` means the editor was dismissed without an explicit pick or clear - leave the cell's value untouched. */
  getValue(): TOption | null | undefined {
    return this.#selected;
  }

  afterGuiAttached(): void {
    this.searchInputRef()?.nativeElement.focus();
  }

  displayValue(option: TOption): string {
    return this.#params.displayValue(option);
  }

  optionId(option: TOption): string {
    return this.#params.optionId(option);
  }

  optionDomId(index: number): string {
    return `${this.listboxId}-opt-${index}`;
  }

  specialRowId(index: number): string {
    return `${this.listboxId}-special-${index}`;
  }

  /** Converts a special row's position in `specialRows()` to its `highlightedIndex` value (negative, in the same top-to-bottom render order, with the row right above the item list at -1). */
  specialIndex(position: number): number {
    return position - this.specialRows().length;
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.search.set(value);
    clearTimeout(this.#searchDebounceTimer);
    this.#searchDebounceTimer = setTimeout(() => void this.#loadFirstPage(), 250);
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
    if (nearBottom && !this.loading() && this.items().length < this.totalCount()) {
      void this.#loadNextPage();
    }
  }

  onKeydown(event: KeyboardEvent): void {
    const minIndex = -this.specialRows().length;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      this.highlightedIndex.update((i) => Math.min(i + 1, this.items().length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      this.highlightedIndex.update((i) => Math.max(i - 1, minIndex));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const index = this.highlightedIndex();
      if (index < 0) {
        const specials = this.specialRows();
        const row = specials[index + specials.length];
        if (row?.kind === "clear") this.clearSelection();
        else if (row?.kind === "custom") this.selectCustomValue(row.label);
      } else {
        const item = this.items()[index];
        if (item) this.selectItem(item);
      }
    }
  }

  selectItem(item: TOption): void {
    this.#selected = item;
    this.#params.api.stopEditing();
  }

  clearSelection(): void {
    this.#selected = null;
    this.#params.api.stopEditing();
  }

  selectCustomValue(label: string): void {
    const create = this.#params.createCustomOption;
    if (!create) return;
    this.#selected = create(label);
    this.#params.api.stopEditing();
  }

  async #loadFirstPage(): Promise<void> {
    const token = ++this.#requestToken;
    const search = this.search();
    this.loading.set(true);
    try {
      const [count, page] = await Promise.all([
        this.#params.fetchCount(search),
        this.#params.fetchItems(search, this.#pageSize, 0),
      ]);
      if (token !== this.#requestToken) return;
      this.totalCount.set(count);
      this.items.set(page);
      this.highlightedIndex.set(this.#defaultHighlightIndex());
    } finally {
      if (token === this.#requestToken) this.loading.set(false);
    }
  }

  async #loadNextPage(): Promise<void> {
    const token = ++this.#requestToken;
    const search = this.search();
    const offset = this.items().length;
    this.loading.set(true);
    try {
      const page = await this.#params.fetchItems(search, this.#pageSize, offset);
      if (token !== this.#requestToken) return;
      this.items.update((items) => [...items, ...page]);
    } finally {
      if (token === this.#requestToken) this.loading.set(false);
    }
  }
}
